import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

/**
 * MS556: Dashboard pool-status widget hooks.
 *
 * Two queries feed the widget:
 *   1. useCurrentBudgetPeriod — the active company_budget_periods row
 *      for the company (the period whose [period_start, period_end]
 *      window contains today). Returns null when no row exists; the
 *      widget should hide.
 *   2. useBudgetSpentInPeriod — three sums layered over the active
 *      period: SUM(orders.budget_cents) for "spent", same SUM filtered
 *      to in-flight statuses for "scheduled", and SUM(adjustments) for
 *      the net top-up/reduce delta against the allocation. Composed
 *      into a single hook so the widget gets one render with all three
 *      values resolved together (rather than three independent queries
 *      that finish at different paint moments).
 *
 * Schema reference: company_budget_periods + company_budget_adjustments
 * (landed in MS553). Columns confirmed in DB:
 *   - company_budget_periods: allocated_cents, carried_over_cents
 *   - company_budget_adjustments: amount_cents (signed), kind
 *
 * Why the cents columns: orders.budget was migrated to orders.budget_cents
 * by MS551-followup-A (already in main). All monetary maths here is pure
 * integer cents — no float dollars round-trip.
 *
 * RLS: both new tables are admin-scoped per the MS553 migration; the
 * widget surface is admin-only per MS552 §4 + §9, so anon/non-admin
 * users hitting this hook get a silent empty result (the page-level
 * guard hides the widget before they get here).
 */

/**
 * A company_budget_periods row. Mirrors the live schema. Kept as a
 * local interface rather than the generated supabase types because the
 * generated types file is regenerated post-merge — pinning the shape
 * here insulates this hook from a stale types file in the worktree.
 */
export interface CompanyBudgetPeriod {
  id: string;
  company_id: string;
  period_start: string; // YYYY-MM-DD
  period_end: string; // YYYY-MM-DD
  cadence_type: "per_celebration" | "weekly" | "monthly" | "quarterly";
  allocated_cents: number;
  carried_over_cents: number;
  created_at: string;
}

/**
 * Reads the active period row for a company. "Active" = today falls
 * within [period_start, period_end] inclusive.
 *
 * Returns null when no row exists (per-celebration cadence, or
 * unconfigured company that hasn't lazy-created its first period yet).
 * The widget treats null the same as `pool_cadence === 'per_celebration'`
 * — hide entirely.
 *
 * Does not lazy-create the period — that responsibility belongs to the
 * Settings → Budget panel (MS553/MS554) when the buyer saves cadence.
 * MS556's widget is read-only.
 */
export function useCurrentBudgetPeriod(companyId: string | null | undefined) {
  return useQuery({
    queryKey: ["budget-period-current", companyId],
    enabled: Boolean(companyId),
    queryFn: async (): Promise<CompanyBudgetPeriod | null> => {
      if (!companyId) return null;
      // Use today's date (ISO YYYY-MM-DD) for the bound check. The
      // server-side comparison is date-typed so a YYYY-MM-DD string
      // round-trips cleanly without timezone drift.
      const today = new Date().toISOString().split("T")[0];
      const { data, error } = await supabase
        // Generated types don't yet know about this table (the types
        // file is regenerated post-merge). Cast through `from` to
        // unblock the typed select. Switch to typed once types
        // catch up after MS554 lands.
        .from("company_budget_periods" as never)
        .select(
          "id, company_id, period_start, period_end, cadence_type, allocated_cents, carried_over_cents, created_at",
        )
        .eq("company_id", companyId)
        .lte("period_start", today)
        .gte("period_end", today)
        .maybeSingle();
      if (error) {
        // Don't throw on PGRST116 (no rows) — maybeSingle returns
        // {data: null, error: null} for that case. Any other error
        // (RLS denial, schema cache miss) does throw so the widget's
        // error state shows the operator something's wrong.
        throw error;
      }
      return (data as unknown as CompanyBudgetPeriod | null) ?? null;
    },
  });
}

/**
 * Aggregated spent/scheduled/adjustments for a single period. Returns
 * all three so the widget renders in one paint.
 *
 * - spentCents: SUM(orders.budget_cents) within [period_start, period_end],
 *   excluding refunded orders. Maps to the "Spent so far" headline.
 * - scheduledCents: same SUM filtered to in-flight order statuses
 *   (pending | approved | processing). Maps to "Scheduled but not
 *   shipped." Excludes completed/shipped because those are already
 *   counted in spent — they share the same row, only the status differs.
 * - adjustmentsCents: signed SUM of company_budget_adjustments.amount_cents
 *   for the period. Positive = top-ups exceed reductions.
 */
export interface BudgetPeriodTotals {
  spentCents: number;
  scheduledCents: number;
  adjustmentsCents: number;
}

export function useBudgetSpentInPeriod(
  companyId: string | null | undefined,
  periodId: string | null | undefined,
  periodStart: string | null | undefined,
  periodEnd: string | null | undefined,
) {
  return useQuery({
    queryKey: [
      "budget-period-totals",
      companyId,
      periodId,
      periodStart,
      periodEnd,
    ],
    enabled: Boolean(companyId && periodId && periodStart && periodEnd),
    queryFn: async (): Promise<BudgetPeriodTotals> => {
      if (!companyId || !periodId || !periodStart || !periodEnd) {
        return { spentCents: 0, scheduledCents: 0, adjustmentsCents: 0 };
      }
      // Period bounds use `created_at` against the date column. We add
      // one day to period_end so the window is inclusive of the final
      // day's orders (orders' created_at is a timestamptz; the period
      // is a date). Mirrors MS551 §7's SQL semantics.
      const periodEndExclusive = (() => {
        const d = new Date(`${periodEnd}T00:00:00Z`);
        d.setUTCDate(d.getUTCDate() + 1);
        return d.toISOString();
      })();
      const periodStartIso = `${periodStart}T00:00:00Z`;

      // Spent: every order in the period that isn't refunded.
      // Scheduled: subset of spent that's still in flight. We fetch
      // budget_cents + status + payment_status for the period once and
      // bucket on the client so we only round-trip once.
      const ordersRes = await supabase
        .from("orders")
        .select("budget_cents, status, payment_status")
        .eq("company_id", companyId)
        .gte("created_at", periodStartIso)
        .lt("created_at", periodEndExclusive);
      if (ordersRes.error) throw ordersRes.error;
      const orders =
        (ordersRes.data as unknown as Array<{
          budget_cents: number | null;
          status: string | null;
          payment_status: string | null;
        }> | null) ?? [];

      const IN_FLIGHT_STATUSES = new Set(["pending", "approved", "processing"]);
      let spentCents = 0;
      let scheduledCents = 0;
      for (const o of orders) {
        // Refunded orders never count toward spent or scheduled (the
        // buyer effectively didn't pay for them). Matches MS551 §7's
        // exclusion.
        if (o.payment_status === "refunded") continue;
        const cents = o.budget_cents ?? 0;
        spentCents += cents;
        if (o.status && IN_FLIGHT_STATUSES.has(o.status)) {
          scheduledCents += cents;
        }
      }

      // Adjustments: signed sum of every audit log row attached to this
      // period. Sign is encoded in amount_cents directly (positive =
      // top-up, negative = reduction) per MS553 schema.
      const adjRes = await supabase
        .from("company_budget_adjustments" as never)
        .select("amount_cents")
        .eq("period_id", periodId);
      if (adjRes.error) throw adjRes.error;
      const adjustments =
        (adjRes.data as unknown as Array<{ amount_cents: number }> | null) ?? [];
      const adjustmentsCents = adjustments.reduce(
        (acc, a) => acc + (a.amount_cents ?? 0),
        0,
      );

      return { spentCents, scheduledCents, adjustmentsCents };
    },
  });
}
